import { Box, Grid, darken, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FC, useCallback, useEffect, useState } from 'react';
import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { UnsavedChanges } from '../../components/modal/UnsavedChanges';
import { AdminTable } from '../../components/table/AdminTable';
import { useToastContext } from '../../hooks';
import { theme } from '../../styles';
import { deepEqual } from 'fast-equals';
import { PropertyTypes } from './PropertyTypes';
import { PropertySubTypeAPI } from '../../fetch/property-subtypes';
import {
  IParentPropertySubType,
  IPropertySubType,
  PropertySubTypeFactory,
} from '../../models/property-subtypes';

/**
 * Presentational component allowing a user to click a Commission Table on the
 * left, auto-hiding and showing data relevant to that table ID in the
 * right-side column.
 */
export const PropertySubTypes: FC = () => {
  // Non-state
  const { showToast } = useToastContext();
  const classes = useStyles();

  // Network state
  const [selectedProperty, setSelectedProperty] = useState<IParentPropertySubType | null>(null);
  const [subTypes, setSubTypes] = useState<IParentPropertySubType[]>([]);
  const [subTypesCopy, setSubTypesCopy] = useState<IParentPropertySubType[]>([]);

  // Non-network state
  const [newElementIndex, setNewElementIndex] = useState(-1);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<false | IPropertySubType>(false);

  const fetchProperties = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await PropertySubTypeAPI.getAll();

      setSubTypes(res);
      setSubTypesCopy(JSON.parse(JSON.stringify(res)));
      // Select a default table
      if (res.length > 0 && !selectedProperty)
        setSelectedProperty(prev => {
          if (!!prev) return prev;
          return res[0];
        });
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch property subtypes.');
    } finally {
      setIsLoading(false);
    }
    // Ignore showToast dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProperties();
    // Fetch only on component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeSubTypeRow = (index: number) => {
    setSubTypes((state: IParentPropertySubType[]) => {
      const property = state.find(
        (state: IParentPropertySubType) => state.propertyTypeId === selectedProperty?.propertyTypeId
      );

      if (typeof property !== 'undefined') {
        property.subTypes.splice(index, 1);
      }
      setNewElementIndex(-1);
      return JSON.parse(JSON.stringify(state));
    });
  };

  const propertySubTypeColumns = [
    {
      accesor: 'name',
      Header: 'Name',
      verticalAlignment: 'top',
      sort: false,
      Cell: ({
        cell: {
          row: { original, index },
        },
      }: {
        cell: { row: { original: IPropertySubType; index: number } };
      }) => {
        const [propertySubTypeName, setPropertySubTypeName] = useState(original.name ?? '');

        return (
          <Box>
            {/* {console.log(index)} */}
            <TextField
              fullWidth
              autoFocus
              inputProps={{
                id: `${'Property-SubType-Name'}-${index}`,
              }}
              placeholder={'Property SubType Name'}
              value={propertySubTypeName}
              onChange={event => {
                setPropertySubTypeName(event.target.value);
                setSubTypes((state: IParentPropertySubType[]) => {
                  const property = state.find(
                    state => state.propertyTypeId === selectedProperty?.propertyTypeId
                  );

                  if (typeof property !== 'undefined') {
                    property.subTypes[index].name = event.target.value;
                    property.subTypes[index].isEdited = true;
                  }
                  setNewElementIndex(-1);
                  return state;
                });
              }}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Page title="Property Subtypes">
      <Box
        sx={theme => ({
          display: 'flex',
          width: '100%',
          height: '100%',
          margin: '0 auto',
        })}
      >
        <UnsavedChanges dirty={hasChanges} />

        {/** CONTAINER OF BOTH TABLES */}
        <Grid container direction="row" gap="5rem">
          {/** LEFT CONTAINER */}
          <Grid
            item
            sx={theme => ({
              maxWidth: '18rem',
              borderRightStyle: 'solid',
              borderWidth: '2px',
              borderColor: theme.frankie.secondary,
              flexGrow: 1,
              '.MuiTableCell-root': {
                paddingRight: '0',
              },
              paddingBlock: theme.spacing(1),
            })}
          >
            <PropertyTypes
              propertySubTypes={subTypes}
              setPropertySubTypes={setSubTypes}
              propertySubTypesCopy={subTypesCopy}
              setSelectedPropertySubType={setSelectedProperty}
              selectedPropertySubType={selectedProperty}
              setNewElementIndex={setNewElementIndex}
              newElementIndex={newElementIndex}
              hasChanges={hasChanges}
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            sx={theme => ({
              flexGrow: 1,
              '.MuiTableRow-root .MuiTableCell-root:first-of-type': {
                paddingLeft: '0',
              },
            })}
          >
            <AdminTable
              data={
                subTypes.find(_ => _.propertyTypeId === selectedProperty?.propertyTypeId)
                  ?.subTypes ?? []
              }
              columns={propertySubTypeColumns}
              isLoading={isLoading}
              headerClasses={classes.header}
              hasChanges={!deepEqual(subTypes, subTypesCopy)}
              isSaving={isSaving}
              onAddFocusClass="property-subtypes-input"
              onAdd={() => {
                if (!selectedProperty) return;
                // Add new empty row
                setSubTypes((state: IParentPropertySubType[]) => {
                  const propertyIndex = state.findIndex(
                    _ => _.propertyTypeId === selectedProperty.propertyTypeId
                  );
                  if (propertyIndex === -1) {
                    return state;
                  }
                  const cloned = state.slice();
                  setNewElementIndex(cloned[propertyIndex].subTypes.length);

                  const { propertyTypeId } = selectedProperty;
                  cloned[propertyIndex].subTypes.push(
                    PropertySubTypeFactory.getDefaultSubType({ propertyTypeId })
                  );
                  return cloned;
                });
              }}
              onSave={async function upsertPropertySubTypes() {
                setIsSaving(true);
                try {
                  const cleanedSubTypes = subTypes.map(propertySubType => {
                    if (propertySubType.propertyTypeId < 0) {
                      propertySubType.propertyTypeId = 0;
                      propertySubType.subTypes.map(tier => {
                        if (tier.propertySubTypeId < 0) {
                          tier.subTypeTableId = 0;
                        }
                        return tier;
                      });
                    }
                    return propertySubType;
                  });

                  const newSubTypes = await PropertySubTypeAPI.upsert(cleanedSubTypes);
                  // After successful update, update UI
                  setSubTypes(newSubTypes);
                  setSubTypesCopy(JSON.parse(JSON.stringify(newSubTypes)));
                  // Reset save button to disabled
                  setHasChanges(false);
                  showToast('success', 'Successfully saved Property SubType.');
                } catch (error) {
                  const message =
                    error instanceof Error ? error.message : 'Failed to save Property SubType.';
                  showToast('error', message);
                } finally {
                  setIsSaving(false);
                }
              }}
              onDelete={(subType, _index) => {
                // If deleting a new tier (ID = 0), do not pop modal, just delete and pop toast.
                if (subType.propertySubTypeId === 0) {
                  removeSubTypeRow(_index);
                  showToast('info', 'Removed unsaved Property SubType.');
                } else {
                  // Pop the delete confirm modal and set the data-to-delete
                  setEntryToDelete(subType);
                }
              }}
            />
          </Grid>
        </Grid>
      </Box>
      {!!entryToDelete && (
        <ConfirmationDialogue
          open
          id="delete-property-subtype-tier"
          title="Delete Property SubType"
          onClose={() => setEntryToDelete(false)}
          onConfirm={async function deletePropertySubtype() {
            // Ensure data exists
            if (!entryToDelete) {
              return;
            }
            // Hit the API and delete
            try {
              await PropertySubTypeAPI.delete(entryToDelete.propertySubTypeId);
              // Update UI after successful delete
              fetchProperties();
              showToast('success', 'Successfully deleted Property SubType.');
            } catch (err: any) {
              const message = Boolean(err.detail)
                ? err.detail
                : 'Failed to delete Property Sub Type.';
              showToast('error', message);
            } finally {
              setEntryToDelete(false);
            }
          }}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  header: {
    textAlign: 'center',
  },
  addCommissionTableButton: {
    transition: 'padding 160ms ease-in',
    width: '100%',
    color: theme.frankie.searchBlue,
    backgroundColor: theme.palette.grey[300],
    padding: theme.spacing(0.465),
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'left',
    marginTop: 6,
    justifyContent: 'space-around',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,

    '&:hover': {
      backgroundColor: darken(theme.palette.grey[300], 0.1),
      paddingInline: theme.spacing(1, 0.535),
    },
  },
  CommissionTableName: {
    margin: theme.spacing(1, 0),
  },
}));
